import config from 'customer-flows/config/environment'
import { Tracker } from 'customer-flows/services/trackers/tracker'
import { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import { context } from 'customer-flows/utils/core'
import { isMobileDevice } from 'customer-flows/utils/is-mobile-device'

import type FrameManagerService from 'customer-flows/services/frame-manager'

import mixpanel from 'mixpanel-browser'
import AvailabilityStore from 'customer-flows/stores/availability.ts'
import type ActivityStore from 'customer-flows/stores/activity.ts'

export default class MixpanelTracker extends Tracker {
  @tracked isSetup = false
  @service frameManager: FrameManagerService

  @context('application') legacyProgramId: ApplicationStore['legacyProgramId']
  @context('account:name') accountName: AccountStore['name']
  @context('account') stripeData: AccountStore['stripeData']
  @context('activity') id: ActivityStore['id']
  @context('activity') type: ActivityStore['type']
  @context('activity') name: ActivityStore['name']
  @context('activity') tickets: ActivityStore['tickets']
  @context('availability') selectedDate: AvailabilityStore['selectedDate']
  @context('availability') selectedAvailabilityTime: AvailabilityStore['selectedAvailabilityTime']
  @context('availability') daysInAdvance: AvailabilityStore['daysInAdvance']
  @context('availability') availabilityForSelectedDate: AvailabilityStore['availabilityForSelectedDate']
  @context('availability:currency') availabilityCurrency: AvailabilityStore['currency']
  @context('custom-question') prePaymentQuestionCount: CustomQuestionStore['prePaymentQuestionCount']
  @context('custom-question') postPaymentQuestionCount: CustomQuestionStore['postPaymentQuestionCount']
  @context('customer:email') customerEmail: CustomerStore
  @context('customer:phone') customerPhone: CustomerStore
  @context('customer:name') customerName: CustomerStore
  @context('customer:optinSms') customerOptinSms: CustomerStore
  @context('customer:optinMarketing') customerOptinMarketing: CustomerStore
  @context('payment') availableExpressCheckoutMethods: PaymentStore['availableExpressCheckoutMethods']
  @context('payment') paymentMethod: PaymentStore['paymentMethod']
  @context('program:*') programStore: ProgramStore
  @context('program/booking-flow') showTicketSection: BookingFlowStore['showTicketSection']
  @context('program/booking-flow') disableCountdownClock: BookingFlowStore['disableCountdownClock']
  @context('program/booking-flow') promoCodeDisplay: BookingFlowStore['promoCodeDisplay']
  @context('program/booking-flow') promoCodeLocation: BookingFlowStore['promoCodeLocation']
  @context('program/booking-flow') disableAutoAddTicket: BookingFlowStore['disableAutoAddTicket']
  @context('program/booking-flow') enableMembershipRedemption: BookingFlowStore['enableMembershipRedemption']
  @context('quote') addonLines: QuoteStore['addonLines']
  @context('quote') hasPeekProtect: QuoteStore['hasPeekProtect']
  @context('quote') showPeekProtectSuggestion: QuoteStore['showPeekProtectSuggestion']
  @context('quote') priceBreakdown: QuoteStore['priceBreakdown']
  @context('quote') peekProtectFee: QuoteStore['peekProtectFee']
  @context('tickets') confirmedTickets: TicketsStore['confirmedTickets']

  protected readonly supportedEvents: TrackingEventName[] = [
    'Complete',
    'Component Seen: Input Billing',
    'Component Seen: Input Info',
    'Peek Protect Clicked',
    'Back Button Navigation Clicked',
    'Start',
    'tile_clicked',
    'Selected Calendar Date',
    'Selected Calendar Time',
    'Selected Ticket',
    'Selected Duration',
    'Selected Payment Method',
    'ngw_payment_submission'
  ]

  setup(): void {
    if (config.mixpanel?.key) {
      mixpanel.init(config.mixpanel.key)

      this.isSetup = true
    }
  }

  get isEnabled(): boolean {
    return this.isSetup
  }

  track<E extends TrackingEventName>(event: E, props: EventProps<E>): void {
    if (!this.isEnabled) {
      return
    }

    mixpanel.track(event, {
      activityID: this.id,
      activityMode: this.type,
      activityName: this.name,
      activityTime: this.selectedAvailabilityTime ?? null,
      bookingFlowLayout: this.programStore.layoutName,
      cip: config.clientIP ?? null,
      currency: this.priceBreakdown?.total?.currency ?? this.availabilityCurrency ?? null,
      daysInAdvance: this.daysInAdvance ?? null,
      disableAutoAddTicket: this.disableAutoAddTicket,
      disableCountdownClock: this.disableCountdownClock,
      enableMembershipRedemption: this.enableMembershipRedemption,
      hasEmail: Boolean(this.customerEmail),
      hasName: Boolean(this.customerName),
      hasPhone: Boolean(this.customerPhone),
      hasPeekProtect: this.hasPeekProtect,
      hasTips: null, // implement this whenever tips are enabled
      initialFlowLayout: this.programStore.initialLayoutName,
      isMobile: isMobileDevice(),
      legacyProgramId: this.legacyProgramId ?? null,
      locale: this.programStore.locale,
      memberIdEntered: null,
      mode: this.frameManager.trackingMode,
      numAddonTicketsInCart: this.addonLines.reduce((total, line) => total + (line.quantity ?? 0), 0),
      numAvailabilityTimes: this.availabilityForSelectedDate.length ?? 0,
      numBundleItemsInCart: null, // implement this when bundles are ready
      numOptBundlesAvailable: null, // implement this when opt bundles are ready
      numPossibleAddons: null, // implement this when add-ons are ready
      numPossibleTickets: this.tickets.length ?? 0,
      numQuestionsAtBeginning: this.prePaymentQuestionCount ?? 0,
      numQuestionsAtEnd: this.postPaymentQuestionCount ?? 0,
      numReqBundlesAvailable: null, // implement this when req bundles are ready
      numTicketsInCart: this.confirmedTickets.length ?? 0,
      optinMarketing: this.customerOptinMarketing ?? null,
      optinSms: this.customerOptinSms ?? null,
      orderTaxesFeesBreakdownMode: this.programStore.orderTaxesFeesBreakdownMode,
      partnerID: this.programStore.partnerId,
      partnerName: this.accountName,
      paymentProcessor: this.stripeData?.type ?? null,
      paymentType: this.paymentMethod?.type ?? null,
      peekProtectFee: this.peekProtectFee?.amount ? parseFloat(this.peekProtectFee.amount) : null,
      peekProtectSuggestion: this.showPeekProtectSuggestion,
      pricingTransparency: this.programStore.pricingTransparency,
      promoCodeDisplay: this.promoCodeDisplay ?? null,
      promoCodeLocation: this.promoCodeLocation ?? null,
      revenue: this.priceBreakdown?.total?.amount ? parseFloat(this.priceBreakdown.total.amount) : null,
      selectedDate: this.selectedDate ?? null,
      showTicketSection: this.showTicketSection,
      source: this.frameManager.source,
      stripeExpressCheckoutMethods: this.availableExpressCheckoutMethods?.join(',') ?? null,
      theme: this.programStore.theme,
      tippingAdded: null,
      tippingVisible: null,
      widgetId: this.id,
      ...props
    })
  }
}
